import Vue from "vue";
import VueRouter from "vue-router";
import Home from "@/views/Home.vue";
import store from "@/store/index.js";
import i18n from "../i18n";
import { translate, setLanguage, availableLanguage} from '@/helpers/translationsHelpers.js'
Vue.use(VueRouter);

const routes = [
  {
    path: "/:language([a-z]{2})/resorts",
    name: "Resorts",
    component: () =>
      import(/* webpackChunkName: "views-resorts" */ "@/views/Resorts.vue"),
    meta: {
      navigation: {
        title: () => store.getters['resort/resorts'].length > 1 ? i18n.t("resorts.choose") : ''
      }
    },
    async beforeEnter (to, from, next) {
      store.dispatch('order/resetDetails')
      store.dispatch('resort/resetCatalogAndProduct')
      store.dispatch('resort/resetResort')
      await store.dispatch('resort/loadResorts', {code: window.location.host})
      next()
    },
  },

  {
    path: "/:language([a-z]{2})/no-products",
    name: "NoProducts",
    component: () =>
      import(/* webpackChunkName: "views-resorts" */ "@/views/NoProducts.vue"),
    meta: {
      analytics: {
        realPath: (route) => `/${route.params.language}/resorts/${store.getters['resort/resort'].id}/no-products`
      },
      navigation: {
        title: () => i18n.t("catalog.forfaits"),
        left: {
          component: () => import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue"),
          props: {
            text: () => i18n.t("resorts.resorts"),
            routeName: 'Resorts'
          }
        }
      }
    }
  },

  {
    path: "/:language([a-z]{2})/results",
    name: "Results",
    component: () =>
      import(/* webpackChunkName: "views-cart" */ "@/views/Results.vue"),
    meta: {
      analytics: {
        realPath: (route) => `/${route.params.language}/resorts/${store.getters['resort/resort'].id}/results`
      },
      navigation: {
        title: () => "",
      }
    }
  },

  {
    path: "/:language([a-z]{2})",
    name: "Home",
    component: Home,
    children: [
      {
        path: "about",
        name: "About",
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "views-about" */ "@/views/About.vue")
      },

      {
        path: "orders",
        name: "Orders",
        component: () =>
          import(/* webpackChunkName: "views-cart" */ "@/views/Orders.vue"),
        meta: {
          analytics: {
            realPath: (route) => route.fullPath.replace('/orders', `/resorts/${store.getters['resort/resort'].id}/orders`)
          },
          navigation: {
            title: () => i18n.t("myorders.myorders"),
            subtitle: () => i18n.t("myorders.check"),
            left: {
              component: () => import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue"),
              props: {
                text: () => i18n.t("catalog.choose"),
                routeName: 'Catalog',
                custom: 'close'
              }
            }
          }
        },
        beforeEnter (to, from, next) {
          store.dispatch('order/ordersUser', {id: store.getters['auth/user'].id}).then(() => {
            next()
          })
        },
      },

      {
        path: "order/details",
        name: "Order details",
        component: () =>
          import(/* webpackChunkName: "views-cart" */ "@/views/OrderDetails.vue"),
        meta: {
          analytics: {
            realPath: (route) => route.fullPath.replace('/details', `/resorts/${store.getters['resort/resort'].id}/order/details`)
          },
          navigation: {
            title: () => i18n.t("myorders.details"),
            subtitle: () => i18n.t("myorders.check_details"),
            left: {
              component: () => import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue"),
              props: {
                text: () => i18n.t("myorders.myorders"),
                routeName: 'Orders',
                custom: 'close'
              }
            }
          }
        },
      },

      {
        path: "resorts/catalog",
        name: "Catalog",
        component: () =>
          import(/* webpackChunkName: "views-catalog" */ "@/views/Catalog.vue"),
        meta: {
          analytics: {
            realPath: (route) => route.fullPath.replace('/catalog', `/${store.getters['resort/resort'].id}/catalog`)
          },
          navigation: {
            title: () => store.getters['resort/resort'].name,
            subtitle: () => i18n.t("catalog.choose"),
            left: {
              component: () => store.getters['resort/resorts'].length !== 1 ? import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue") : null,
              props: {
                text: () => i18n.t("resorts.choose"),
                routeName: 'Resorts'
              }
            },
            right: {
              component: () => import(/* webpackChunkName: "myorders-logout" */ "../components/layout/myorders-logout.vue"), 
              props: {
                routeName: 'Orders',
                showOrders: true,
                showLogout: true
              }
            }
          }
        },
        beforeEnter (to, from, next) {

          store.dispatch('order/resetDetails')
          store.dispatch('resort/resetCatalogAndProduct')

          if (!store.getters['resort/resort']) {
            next({ name: 'Resorts', params: { language: to.params.language } })
          }
          if (!store.getters['resort/resort'].active || store.getters['resort/catalogsWithActiveProducts'].length === 0) {
            next({ name: 'NoProducts', params: { language: to.params.language } })
          } else {
            next()
          }
        },
      },

      {
        path: "steps",
        name: "Steps",
        component: () => import(/* webpackChunkName: "views-steps" */ "@/views/Steps.vue"),
        beforeEnter (to, from, next) {
          if (store.getters['order/details'].length > 0) {
            store.dispatch('order/restoreDetails')
          }

          if(to.params.catalogid !== undefined && to.params.productid !== undefined){
            store.dispatch('resort/selectCatalogAndProduct', {
              catalogId: to.params.catalogid,
              productId: to.params.productid
            })
          }

          if (!store.getters['resort/selectedProduct'] || !store.getters['resort/selectedCatalog']) {
            next({ name: 'Catalog', params: { language: to.params.language } })
          }

          next()
        },
        meta: {
          analytics: {
            realPath: (route) => route.fullPath.replace('/steps', `/resorts/${store.getters['resort/resort'].id}/steps`)
          },
          navigation: {
            title: () => translate(store.getters['resort/selectedProduct'], 'name'),
            subtitle: () => i18n.t("when.subtitle"),
            left: {
              component: () => import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue"),
              props: {
                text: () => i18n.t("catalog.choose"),
                routeName: 'Catalog'
              }
            },
            right: {
              component: () => import(/* webpackChunkName: "myorders-logout" */ "../components/layout/myorders-logout.vue"), 
              props: {
                routeName: 'Orders',
                showOrders: false,
                showLogout: true
              }
            }
          }
        }
      },

      {
        path: "participants",
        name: "Participants",
        component: () =>
          import(/* webpackChunkName: "views-participants" */ "@/views/participants/Participants.vue"),
        meta: {
          analytics: {
            realPath: (route) => route.fullPath.replace('/participants', `/resorts/${store.getters['resort/resort'].id}/participants`)
          },
          navigation: {
            title: () => i18n.t("people.people"),
            subtitle: () => i18n.t("participants.dataskier"),
            left: {
              component: () => import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue"),
              props: {
                text: () => i18n.t("when.title"),
                routeName: 'Steps'
              }
            },
            right: {
              component: () => import(/* webpackChunkName: "myorders-logout" */ "../components/layout/myorders-logout.vue"), 
              props: {
                routeName: 'Orders',
                showOrders: false,
                showLogout: true
              }
            }
          }
        },
        beforeEnter (to, from, next) {
          store.dispatch('prices/resetPromoCode')
          if (store.getters['order/details'].length === 0) {
            next({ name: 'Steps', params: { language: to.params.language } })
          }
          next()
        },
      },

      {
        path: "photo/:participantIndex",
        name: "Photo",
        component: () =>
          import(/* webpackChunkName: "views-photo" */ "@/views/Photo.vue"),
        meta: {
          analytics: {
            realPath: (route) => route.fullPath.replace('/photo', `/resorts/${store.getters['resort/resort'].id}/participants/photo`)
          },
          navigation: {
            title: () => i18n.t("participants.photo.photo"),
            subtitle: () => i18n.t("participants.photo.add"),
            left: {
              component: () => import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue"),
              props: {
                text: () => '',
                routeName: 'Participants'
              }
            },
            right: {
              component: () => import(/* webpackChunkName: "myorders-logout" */ "../components/layout/myorders-logout.vue"), 
              props: {
                routeName: 'Orders',
                showOrders: false,
                showLogout: true
              }
            }
          }
        },
        beforeEnter (to, from, next) {
          if (store.getters['order/details'][to.params.participantIndex] === undefined) {
            next({ name: 'Participants', params: { language: to.params.language } })
          }
          next()
        },
      },

      {
        path: "keycard/:participantIndex",
        name: "Keycard",
        component: () =>
          import(/* webpackChunkName: "views-keycard" */ "@/views/Keycard.vue"),
        meta: {
          analytics: {
            realPath: (route) => route.fullPath.replace('/keycard', `/resorts/${store.getters['resort/resort'].id}/participants/keycard`)
          },
          navigation: {
            title: () => i18n.t("participants.keycard.keycard"),
            left: {
              component: () => import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue"),
              props: {
                text: () => '',
                routeName: 'Participants'
              }
            },
            right: {
              component: () => import(/* webpackChunkName: "myorders-logout" */ "../components/layout/myorders-logout.vue"), 
              props: {
                routeName: 'Orders',
                showOrders: false,
                showLogout: true
              }
            }
          }
        },
        beforeEnter (to, from, next) {
          if (store.getters['order/details'][to.params.participantIndex] === undefined) {
            next({ name: 'Participants', params: { language: to.params.language } })
          }
          next()
        },
      },

      {
        path: "keycard/:participantIndex/new",
        name: "KeycardNew",
        component: () =>
          import(/* webpackChunkName: "views-keycard-new" */ "@/views/KeycardNew.vue"),
        meta: {
          analytics: {
            realPath: (route) => route.fullPath.replace('/keycard', `/resorts/${store.getters['resort/resort'].id}/participants/keycard`)
          },
          navigation: {
            title: () => i18n.t("participants.keycard.new"),
            left: {
              component: () => import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue"),
              props: {
                text: () => ''
              }
            },
            right: {
              component: () => import(/* webpackChunkName: "myorders-logout" */ "../components/layout/myorders-logout.vue"), 
              props: {
                routeName: 'Orders',
                showOrders: false,
                showLogout: true
              }
            }
          }
        },
        beforeEnter (to, from, next) {
          if (store.getters['order/details'][to.params.participantIndex] === undefined) {
            next({ name: 'Participants', params: { language: to.params.language } })
          }
          next()
        },
      },

      {
        path: "keycard/:participantIndex/select",
        name: "KeycardSelect",
        component: () =>
          import(/* webpackChunkName: "views-keycard-select" */ "@/views/KeycardSelect.vue"),
        meta: {
          analytics: {
            realPath: (route) => route.fullPath.replace('/keycard', `/resorts/${store.getters['resort/resort'].id}/participants/keycard`)
          },
          navigation: {
            title: () => i18n.t("participants.keycard.keycard"),
            left: {
              component: () => import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue"),
              props: {
                text: () => ''
              }
            },
            right: {
              component: () => import(/* webpackChunkName: "myorders-logout" */ "../components/layout/myorders-logout.vue"), 
              props: {
                routeName: 'Orders',
                showOrders: false,
                showLogout: true
              }
            }
          }
        },
        beforeEnter (to, from, next) {
          if (store.getters['order/details'][to.params.participantIndex] === undefined) {
            next({ name: 'Participants', params: { language: to.params.language } })
          }
          next()
        },
      },

      {
        path: "cart",
        name: "Cart",
        component: () =>
          import(/* webpackChunkName: "views-cart" */ "@/views/Cart.vue"),
        meta: {
          analytics: {
            realPath: (route) => route.fullPath.replace('/cart', `/resorts/${store.getters['resort/resort'].id}/cart`)
          },
          navigation: {
            title: () => i18n.t("review.review"),
            subtitle: () => i18n.t("review.check"),
            left: {
              component: () => import(/* webpackChunkName: "back-button" */ "../components/layout/ria-back-button.vue"),
              props: {
                text: () => i18n.t("people.people"),
                routeName: 'Participants'
              }
            },
            right: {
              component: () => import(/* webpackChunkName: "myorders-logout" */ "../components/layout/myorders-logout.vue"), 
              props: {
                routeName: 'Orders',
                showOrders: false,
                showLogout: true
              }
            }
          }
        }
      }
    ]
  },

  {
    path: "/404",
    name: "not-found",
    component: () => import(/* webpackChunkName: "views-404" */ "@/views/404.vue")
  },

  {
    // Fallback
    path: "*",
    name: "fallback",
    beforeEnter (to, from, next) {
      const lang = availableLanguage(navigator.language) ? availableLanguage(navigator.language) : 'en'
      next({ name: 'Resorts', params: { language: lang }, query: to.query })
    },
  }
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});

router.beforeEach((to, from, next) => {
  setLanguage(to.params.language ? to.params.language : store.getters['device/language'])
  window.scroll(0, 0)
  router.fromPath = from.path;

  if(to.query.testingMode && to.query.testingMode === 'true')
    store.dispatch('auth/setTesting')

  if(to.name !== 'NoProducts' && store.getters['resort/resort']){
    if(store.getters['resort/platformUnavailable'] && !store.getters['auth/testingMode']) {
      next({ name: 'NoProducts', params: { language: to.params.language } })
    }
  }
  next();
});

export default router;
